'use client';

import type { FC } from 'react';
import { ScreenContent, ScreenFixedTop, ScreenTabBar } from '../../screen';
import { Button } from '../../components/ui';
import { InfoCard } from '../../components/ui/InfoCard';
import { RiCheckboxCircleFill } from '../../components/icons';
import { useIsAuthed } from '../../hooks/useIsAuthed';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useLocalizedHref } from '../../hooks/useLocalizedHref';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations, { FREE_SKINS_FAQ_KEYS } from './FreeSkinsScreen.translations';

const STEP_GRADIENTS = [
  'bg-[linear-gradient(135deg,#a855f7_0%,#ec4899_100%)]',
  'bg-[linear-gradient(135deg,#3b82f6_0%,#06b6d4_100%)]',
  'bg-[linear-gradient(135deg,#22c55e_0%,#10b981_100%)]',
  'bg-[linear-gradient(135deg,#eab308_0%,#f97316_100%)]',
];

const TEXT_CLASS = 'm-0 text-sm leading-normal text-[rgb(209,213,219)]';
const LINK_CLASS = 'text-purple-500 font-semibold underline';

const BulletItem: FC<{ text: string }> = ({ text }) => (
  <li className="flex items-start gap-2 text-sm leading-snug text-[rgb(209,213,219)]">
    <RiCheckboxCircleFill size={16} className="mt-0.5 shrink-0 text-purple-500" />
    <span>{text}</span>
  </li>
);

export const FreeSkinsScreen: FC = () => {
  const { t } = useLocalTranslation(translations);
  const isAuthed = useIsAuthed();
  const isDesktop = useIsDesktop();
  const localize = useLocalizedHref();

  const steps = [
    { title: t('step1Title'), text: t('step1Text') },
    { title: t('step2Title'), text: t('step2Text') },
    { title: t('step3Title'), text: t('step3Text') },
    { title: t('step4Title'), text: t('step4Text') },
  ];
  const terms = [t('terms1'), t('terms9'), t('terms2'), t('terms3'), t('terms4'), t('terms5'), t('terms6'), t('terms7'), t('terms8')];
  const other = [t('other1'), t('other2'), t('other3'), t('other4')];
  const links = [
    { href: '/cases', label: t('linkCases') },
    { href: '/achievements', label: t('linkAchievements') },
    { href: '/draws', label: t('linkDraws') },
    { href: '/rains', label: t('linkRains') },
  ];

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenContent>
        <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 pb-8">
          {isDesktop && (
            // WHY: заголовок страницы h1 отдаёт реестр SEO (RouteHeading), здесь он только виден на десктопе, где верхней панели экрана нет
            <p className="m-0 text-2xl font-bold text-white">{t('title')}</p>
          )}
          <p className={TEXT_CLASS}>{t('intro')}</p>

          <div className="flex flex-col items-start gap-2">
            <a href={localize(isAuthed ? '/cases' : '/login')} className="no-underline">
              <Button as="span" size="l">
                {isAuthed ? t('ctaAuthed') : t('ctaGuest')}
              </Button>
            </a>
            {!isAuthed && <p className="m-0 text-xs leading-snug text-label-tertiary">{t('ctaNote')}</p>}
          </div>

          <InfoCard>
            <h2 className="m-0 mb-4 text-lg font-bold text-white">{t('howTitle')}</h2>
            <ol className="m-0 flex list-none flex-col gap-4 ps-0">
              {steps.map((step, i) => (
                <li key={step.title}>
                  <div className="mb-2 flex items-center gap-3">
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${STEP_GRADIENTS[i]}`}>
                      <span className="text-sm font-bold text-white">{i + 1}</span>
                    </div>
                    <h3 className="m-0 text-base font-bold text-white">{step.title}</h3>
                  </div>
                  <p className={TEXT_CLASS}>{step.text}</p>
                </li>
              ))}
            </ol>
          </InfoCard>

          <InfoCard>
            <h2 className="m-0 mb-4 text-lg font-bold text-white">{t('termsTitle')}</h2>
            <ul className="m-0 flex list-none flex-col gap-2 ps-0">
              {terms.map((text) => (
                <BulletItem key={text} text={text} />
              ))}
            </ul>
          </InfoCard>

          <InfoCard>
            <h2 className="m-0 mb-4 text-lg font-bold text-white">{t('useTitle')}</h2>
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="m-0 mb-1 text-base font-bold text-white">{t('sellTitle')}</h3>
                <p className={TEXT_CLASS}>{t('sellText')}</p>
              </div>
              <div>
                <h3 className="m-0 mb-1 text-base font-bold text-white">{t('withdrawTitle')}</h3>
                <p className={TEXT_CLASS}>{t('withdrawText')}</p>
              </div>
            </div>
          </InfoCard>

          <InfoCard>
            <h2 className="m-0 mb-3 text-lg font-bold text-white">{t('otherTitle')}</h2>
            <p className={`${TEXT_CLASS} mb-3`}>{t('otherIntro')}</p>
            <ul className="m-0 flex list-none flex-col gap-2 ps-0">
              {other.map((text) => (
                <BulletItem key={text} text={text} />
              ))}
            </ul>
            <p className={`${TEXT_CLASS} mt-4`}>
              {t('seeAlso')}{' '}
              {links.map((link, i) => (
                <span key={link.href}>
                  {i > 0 && ' · '}
                  <a className={LINK_CLASS} href={localize(link.href)}>
                    {link.label}
                  </a>
                </span>
              ))}
            </p>
          </InfoCard>

          <InfoCard>
            <h2 className="m-0 mb-4 text-lg font-bold text-white">{t('faqTitle')}</h2>
            <div className="flex flex-col gap-4">
              {FREE_SKINS_FAQ_KEYS.map((key) => (
                <div key={key}>
                  <h3 className="m-0 mb-1 text-base font-bold text-white">{t(`${key}Q`)}</h3>
                  <p className={TEXT_CLASS}>{t(`${key}A`)}</p>
                </div>
              ))}
            </div>
          </InfoCard>
        </div>
      </ScreenContent>
    </>
  );
};
